import React, { useEffect, useMemo, useRef, useState } from "react";
import { ButtonGroup } from "reactstrap";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { omit, uniqueId } from "lodash";
import * as Yup from "yup";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import {
  accessoryType,
  getOptionValue,
  natureOfWorkOptions,
  requirementOptions,
} from "common/enums";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";
import Tooltip from "Components/Common/Tooltip";
import { applicationTypes } from "common/constant";

import {
  filterEmptyStrings,
  generateFormData,
  getImageUrl,
  onlyNumberKeys,
} from "utils";
import getFileExtension from "utils/getFileExtension";

import {
  useLazyTechnicalSanctionQuery,
  usePostTechnicalSanctionMutation,
} from "apis/TechnicalSanction";
import { useLazyAccessoriesQuery } from "apis/Accessories";

import { DocumentName, UploadImage } from "./style";

import pdfImg from "assets/images/pdf.png";

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const TechnicalSanctionFormForOtherUtility = ({
  handleClose,
  isTechnicalSanctionModalOpen,
  selectedApplicationDetails,
  t,
}) => {
  //add technical sanction api
  const [addTechnicalSanction, { isLoading }] =
    usePostTechnicalSanctionMutation();

  const [
    getTechnicalSanctionDataByApplicationId,
    {
      isLoading: isTechnicalSanctionDataLoading,
      isFetching: isTechnicalSanctionDataFetching,
      data: { data: technicalSanctionData } = [],
    },
  ] = useLazyTechnicalSanctionQuery();

  // get all accessories from api
  const [
    getAccessoriesData,
    {
      isLoading: isAccessoriesLoading,
      data: { data: accessoriesData = [] } = {},
    },
  ] = useLazyAccessoriesQuery();

  const ref = useRef();
  const [isTechnicallyFeasible, setIsTechnicallyFeasible] = useState(false);

  //after adding technical sanction this will open estimation modal
  useEffect(() => {
    if (
      selectedApplicationDetails?.technicalSanctionId &&
      selectedApplicationDetails?.id
    ) {
      getTechnicalSanctionData(selectedApplicationDetails.id);
    }
  }, [
    selectedApplicationDetails?.technicalSanctionId,
    selectedApplicationDetails.id,
  ]);

  const getTechnicalSanctionData = async (applicationId) => {
    try {
      await getTechnicalSanctionDataByApplicationId(applicationId);
    } catch (error) {}
  };

  const isDcsUtility = useMemo(
    () =>
      selectedApplicationDetails?.utilities[0] ===
      getOptionValue("utilityOptions", "DCS"),
    [selectedApplicationDetails?.utilities[0]]
  );

  //calling api for getting accessories data and Sending Payload if utility is DCS
  useEffect(() => {
    (async () => {
      try {
        const utilityId = isDcsUtility
          ? selectedApplicationDetails?.utilities[0]
          : "";

        const isDTA = !isDcsUtility
          ? selectedApplicationDetails?.utilityAddress?.premiseArea?.id ===
            getOptionValue("premiseAreaEnum", "DTA")
          : "";

        const isTemporaryConnection = !isDcsUtility
          ? selectedApplicationDetails?.applicationType?.id ===
            applicationTypes["Temporary Connection"]
          : "";

        await getAccessoriesData({
          utilityId: utilityId,
          isDTA,
          isTemporaryConnection,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [
    selectedApplicationDetails?.utilities,
    selectedApplicationDetails?.applicationType,
    selectedApplicationDetails?.utilityAddress,
    isDcsUtility,
  ]);

  const initialValuesOfAccessories = (data) => {
    if (data?.length) {
      return data.map(({ id, accessories, type, quantity }) => ({
        id,
        accessoryId: accessories.id,
        type,
        quantity,
      }));
    } else {
      return [
        {
          id: uniqueId("temp_"),
          accessoryId: 0,
          type: "",
          quantity: 1,
        },
      ];
    }
  };

  const initialValues = useMemo(
    () => ({
      id: 0,
      applicationId: selectedApplicationDetails?.id,
      scopeOfWork: technicalSanctionData?.scopeOfWork ?? "",
      remarks: technicalSanctionData?.remark ?? "",
      accessoriesList: initialValuesOfAccessories(
        technicalSanctionData?.accessories || []
      ),
      sourceOfConnection: technicalSanctionData?.sourceOfConnection ?? "",
      technicalJustification:
        technicalSanctionData?.technicalJustification ?? "",
      isTechnicalFeasibility:
        technicalSanctionData?.isTechnicalFeasibility ?? false,
      requirement: technicalSanctionData?.requirement ?? "",
      natureOfWork: technicalSanctionData?.natureOfWork ?? "",
      runningContract: technicalSanctionData?.runningContract ?? "",
      newProcurement: technicalSanctionData?.newProcurement ?? "",
      documents:
        technicalSanctionData?.technicalSanctionAdditionalDocuments?.map(
          ({ path, documentName, id }) => ({
            name: documentName,
            documentUrl: path,
            id,
          })
        ) ?? [],
    }),
    [selectedApplicationDetails?.id, technicalSanctionData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues,
    validationSchema:
      isTechnicallyFeasible &&
      Yup.object().shape({
        scopeOfWork: Yup.string().required("Scope of work is required"),
        ...(!isDcsUtility && {
          requirement: Yup.string().required("Please Select Requirement"),
          natureOfWork: Yup.string().required("Please Select Nature of Work"),
          runningContract: Yup.string().required(
            "Running Contract is required"
          ),
          newProcurement: Yup.string().required("New Procurement is required"),
          technicalJustification: Yup.string().required(
            "Technical justification is required "
          ),
          sourceOfConnection: Yup.string().required(
            "Source of connection is required"
          ),
        }),
        accessoriesList: Yup.array().of(
          Yup.object().shape({
            accessoryId: Yup.number().min(1, "Please select accessory"),
            type: Yup.string()
              .nullable()
              .required("Please select accessory type"),
            quantity: Yup.number()
              .required("Quantity is required")
              .test("isValidCapacity", "", function () {
                const [{ value: current = null } = {}] = this.from || [];
                if (current.quantity === 0) {
                  return this.createError({
                    message: "Quantity must be greater than 0",
                  });
                }
                return true;
              }),
          })
        ),
      }),
    onSubmit: async (values) => {
      try {
        await submitBtnHandler(values, false);
        handleClose();
      } catch (err) {}
    },
  });

  const {
    values: { isTechnicalFeasibility, accessoriesList, documents },
    values,
    setFieldValue,
    handleSubmit,
    setFieldTouched,
  } = formik;

  //decrease quantity
  const handleDecreaseQuantity = (index) => {
    setFieldValue(
      `accessoriesList[${index}].quantity`,
      accessoriesList[index].quantity > 1
        ? +accessoriesList[index].quantity - 1
        : 1
    );
  };

  //increase quantity
  const handleIncreaseQuantity = (index) => {
    setFieldValue(
      `accessoriesList[${index}].quantity`,
      +accessoriesList[index].quantity + 1
    );
  };

  //add more accessories when click on Add accessories
  const handleAddAccessories = (existingAccessories) => {
    const addNewAccessories = {
      id: uniqueId("temp_"),
      type: "",
      accessoryId: 0,
      quantity: 1,
    };
    setFieldValue("accessoriesList", [
      ...existingAccessories,
      addNewAccessories,
    ]);
  };

  //Remove accessories when click on Remove
  const handleRemoveClick = (i) => {
    const accessoryLength = accessoriesList.length || 0;
    if (accessoryLength > 1) {
      setFieldValue(
        "accessoriesList",
        accessoriesList.filter((_, index) => index !== i)
      );
      setFieldTouched(`accessoriesList[${i}]`, false, false);
    }
  };

  const fileChangeHandler = (e) => {
    const files = Array.from(e.target.files);

    const isAllFileValid = files.every(
      (file) =>
        validFileFormats.includes(getFileExtension(file.name.toLowerCase())) &&
        file.size < 10485760
    );

    if (!isAllFileValid) {
      toast.error(
        "Please upload image or pdf file and size should be less than 10 mb"
      );
    } else {
      setFieldValue("documents", [
        ...documents,
        ...files.map((file) => ({
          id: uniqueId("doc__"),
          url: URL.createObjectURL(file),
          name: file.name,
          file: file,
        })),
      ]);
      ref.current.value = "";
    }
  };

  useEffect(() => {
    setIsTechnicallyFeasible(isTechnicalFeasibility);
  }, [isTechnicalFeasibility]);

  const submitBtnHandler = async (values, isDraft = false) => {
    try {
      const setAccessoriesValues = {
        ...values,
        accessoriesList: values.accessoriesList.map((item) => {
          if (item.id.toString().startsWith("temp")) {
            return {
              ...item,
              id: 0,
            };
          }
          return item;
        }),
        technicalSanctionAdditionalDocuments: values.documents.map(
          ({ file, documentUrl, id }) =>
            file ? { id: 0, file } : { id, documentUrl }
        ),
      };

      const payload = {
        ...filterEmptyStrings(setAccessoriesValues),
        isDraft,
      };

      const formData = new FormData();
      generateFormData(formData, omit(payload, "documents"));
      await addTechnicalSanction(formData);
    } catch (err) {
      console.error(err);
    }
  };

  const saveBtnHandler = () => {
    if (accessoriesList.some(({ accessoryId }) => !accessoryId)) {
      toast.error("Please select accessories!");
    } else {
      submitBtnHandler(values, true);
    }
  };

  return (
    <>
      <Backdrop
        open={
          isLoading ||
          isAccessoriesLoading ||
          isTechnicalSanctionDataLoading ||
          isTechnicalSanctionDataFetching
        }
      />
      <Modal
        isOpen={isTechnicalSanctionModalOpen}
        handleClose={handleClose}
        title={t("Generate Technical Sanction")}
        maxWidth="md"
      >
        <div className="fw-semibold my-4">
          <h6>
            {t("Application ID")} :{" "}
            {selectedApplicationDetails?.applicationNumber}
          </h6>
          <h6>
            {t("Application Type")} :{" "}
            {selectedApplicationDetails?.applicationType?.name}
          </h6>
        </div>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              label={t("Technical Feasibility")}
              className="mb-2"
              control={
                <Checkbox
                  checked={isTechnicalFeasibility}
                  onChange={(_, isChecked) => {
                    setFieldValue("isTechnicalFeasibility", isChecked);
                  }}
                />
              }
            />
          </Grid>
        </Grid>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label={t("Scope Of Work") + "*"}
              name="scopeOfWork"
              formik={formik}
              placeholder="Enter Scope Of Work"
              disabled={!isTechnicalFeasibility}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label={t("Remarks")}
              name="remarks"
              formik={formik}
              placeholder="Enter Remarks"
            />
          </Grid>

          {!isDcsUtility && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <SelectField
                  label={t("Requirement") + "*"}
                  name="requirement"
                  formik={formik}
                  options={requirementOptions || []}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  disabled={!isTechnicalFeasibility}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SelectField
                  label={t("Nature of Work") + "*"}
                  name="natureOfWork"
                  formik={formik}
                  options={natureOfWorkOptions || []}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  disabled={!isTechnicalFeasibility}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InputFieldText
                  label={t("Running Contract") + "*"}
                  name="runningContract"
                  formik={formik}
                  placeholder="Enter Running Contract"
                  disabled={!isTechnicalFeasibility}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InputFieldText
                  label={t("New Procurment") + "*"}
                  name="newProcurement"
                  formik={formik}
                  placeholder="Enter New Procurment"
                  disabled={!isTechnicalFeasibility}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InputFieldText
                  label={t("Technical Justification*")}
                  name="technicalJustification"
                  disabled={!isTechnicalFeasibility}
                  formik={formik}
                  placeholder="Enter Technical Justification"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InputFieldText
                  label={t("Source of Connection*")}
                  name="sourceOfConnection"
                  formik={formik}
                  disabled={!isTechnicalFeasibility}
                  placeholder="Enter Source Of Connection"
                />
              </Grid>
            </>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6">{t("Documents")} :</Typography>
        </Grid>
        <Grid item xs={12} className="mb-3 ms-1 mt-1">
          <div className="d-flex gap-3 flex-wrap">
            {!!documents.length &&
              documents.map(
                ({ documentUrl, url, name, id: currentId }, index) => (
                  <div className="image-container" key={index}>
                    <a
                      href={documentUrl ? getImageUrl(documentUrl) : url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Box
                        component="img"
                        className="image d-block m-auto"
                        alt="document image"
                        src={
                          getFileExtension(
                            (documentUrl ? documentUrl : name).toLowerCase()
                          ) === "pdf"
                            ? pdfImg
                            : url ?? getImageUrl(documentUrl)
                        }
                      />
                    </a>
                    <i
                      className="ri-close-circle-line fs-3 close-btn"
                      onClick={() => {
                        setFieldValue(
                          "documents",
                          documents.filter(({ id }) => id !== currentId)
                        );
                      }}
                    />
                    <Tooltip title={name}>
                      <DocumentName fontSize={13} variant="body2">
                        {name}
                      </DocumentName>
                    </Tooltip>
                  </div>
                )
              )}

            <Box component="label" className="m-0 p-0 cursor-pointer">
              <UploadImage isTechnicalFeasibility={isTechnicalFeasibility}>
                <AddIcon
                  sx={{
                    color: isTechnicalFeasibility ? "black" : "#dddddd",
                  }}
                />
              </UploadImage>
              <input
                ref={ref}
                type="file"
                disabled={!isTechnicalFeasibility}
                accept="image pdf/*"
                onChange={fileChangeHandler}
                multiple
                hidden
              />
            </Box>
          </div>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">{t("Accessories")} :</Typography>
        </Grid>
        {accessoriesList.length &&
          accessoriesList.map((data, index) => (
            <Grid
              key={data.id}
              container
              columnSpacing={{ xs: 1 }}
              className="border border-2 ps-2 pb-0 p-3 mb-2"
            >
              <Grid item xs={12} sm={6} md={4}>
                <SelectField
                  label={t("Accessories") + "*"}
                  name={`accessoriesList.${index}.accessoryId`}
                  formik={formik}
                  options={accessoriesData || []}
                  getOptionLabel={(option) => option?.name}
                  getOptionValue={(option) => option?.id}
                  disabled={!isTechnicalFeasibility}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SelectField
                  label={t("Type") + "*"}
                  name={`accessoriesList[${index}].type`}
                  formik={formik}
                  options={accessoryType || []}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  disabled={!isTechnicalFeasibility}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <ButtonGroup
                  size="small"
                  aria-label="small outlined button group"
                >
                  <Button
                    variant="contained"
                    sx={{ minWidth: "40px", height: "40px" }}
                    disabled={
                      !isTechnicalFeasibility ||
                      accessoriesList[index].quantity <= 1
                    }
                    onClick={() => handleDecreaseQuantity(index)}
                  >
                    -
                  </Button>
                  <Box sx={{ width: "110px" }}>
                    <InputFieldText
                      label={t("Quantity")}
                      name={`accessoriesList[${index}].quantity`}
                      formik={formik}
                      type="number"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          onKeyDown: (e) => onlyNumberKeys(e, true),
                        },
                      }}
                      disabled={!isTechnicalFeasibility}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    sx={{ minWidth: "40px", height: "40px" }}
                    onClick={() => handleIncreaseQuantity(index)}
                    disabled={!isTechnicalFeasibility}
                  >
                    +
                  </Button>
                </ButtonGroup>
              </Grid>

              {accessoriesList.length > 1 && (
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    color="error"
                    size="small"
                    sx={{
                      minWidth: "unset",
                      border: "1px solid",
                      height: "40px",
                      width: "100px",
                      mb: 2,
                    }}
                    onClick={() => handleRemoveClick(index)}
                    disabled={!isTechnicalFeasibility}
                  >
                    <strong className="d-flex justify-content-center">
                      <DeleteOutlineSharpIcon fontSize="small" />
                      {t("Remove")}
                    </strong>
                  </Button>
                </Grid>
              )}
            </Grid>
          ))}
        <Box className="d-flex justify-content-between">
          <Button
            color="primary"
            size="small"
            sx={{ minWidth: "unset", textTransform: "capitalize" }}
            variant="outlined"
            onClick={() => handleAddAccessories(accessoriesList)}
            disabled={!isTechnicalFeasibility}
          >
            <strong>
              <span>+</span> {t("Add Accessories")}
            </strong>
          </Button>
          <div className="d-flex align-items-center gap-1">
            <Button
              size="medium"
              variant="outlined"
              disabled={isLoading || !isTechnicalFeasibility}
              onClick={saveBtnHandler}
            >
              {t("Save")}
            </Button>
            <Button
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {t("Submit")}
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

TechnicalSanctionFormForOtherUtility.propTypes = {
  handleClose: PropTypes.func,
  isTechnicalSanctionModalOpen: PropTypes.bool,
  selectedApplicationDetails: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
  t: PropTypes.func,
};

export default withTranslation()(TechnicalSanctionFormForOtherUtility);
